import React, { useMemo } from "react";
import { gql } from "@apollo/client";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import { useTraineeLibraryItemSidebarQuery } from "./TraineeLibraryItemDetailSidebar.generated";
import { TrainingResourceVersionType } from "@src/types.generated";
import MediaPreview from "./courseInstance/MediaPreview";
import ReadOnlyTrainingRichTextDocument from "../libraryItem/shared/ReadOnlyTrainingRichTextDocument";
import { extractBodyContent } from "@src/features/libraryItem/resource/build/TrainingResourceRichTextEditor";
import { Card } from "@src/lib/ui/Card";
import Text from "@src/lib/ui/Text";
import RichTextPreview from "@src/features/messaging/RichTextPreview";
import TranslationModeContextProvider from "@src/features/libraryItem/course/contexts/TranslationModeContextProvider";
import VideoRefMapContextProvider from "@src/features/libraryItem/course/contexts/VideoRefMapContext";
import { ExternalLinkIcon, FileIcon } from "lucide-react";
import Spinner from "@src/lib/ui/Spinner";
import { getLinkType, LinkType } from "@src/lib/utils/files";
import { Button } from "@src/lib/ui/Button";

const normalizeUrl = (url: string): string => {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `https://${url}`;
};

type Props = {
  rowId: string;
  showHeader?: boolean;
};

const TraineeLibraryItemDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const { data, loading } = useTraineeLibraryItemSidebarQuery({
    variables: { id: rowId },
    skip: !rowId,
  });

  const libraryItem = data?.LibraryItem;
  const trainingResource = libraryItem?.trainingResource;
  const publishedVersion = trainingResource?.publishedVersion;

  const isRichTextDocument = useMemo(() => {
    return (
      publishedVersion?.type === TrainingResourceVersionType.RichTextDocument
    );
  }, [publishedVersion?.type]);

  const richTextContent = useMemo(() => {
    if (isRichTextDocument && publishedVersion?.richTextDocument?.content) {
      return extractBodyContent(
        publishedVersion.richTextDocument.content.myTranslation,
      );
    }
    return null;
  }, [isRichTextDocument, publishedVersion?.richTextDocument?.content]);

  const mediaUrl = useMemo(() => {
    const url = publishedVersion?.media?.mediaUrls.en;
    return url ? normalizeUrl(url) : undefined;
  }, [publishedVersion?.media?.mediaUrls.en]);

  const isPdf = useMemo(() => {
    if (!mediaUrl) return false;
    return getLinkType(mediaUrl) === LinkType.PDF;
  }, [mediaUrl]);

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {showHeader && <SidebarHeader />}
        <div className="flex flex-1 items-center justify-center">
          <Spinner size={32} />
        </div>
      </div>
    );
  }

  if (!libraryItem || !trainingResource) {
    return (
      <div className="flex h-full flex-col">
        {showHeader && <SidebarHeader />}
        <div className="flex flex-1 items-center justify-center">
          <Text type="P2" className="text-muted-foreground">
            Resource not found
          </Text>
        </div>
      </div>
    );
  }

  return (
    <TranslationModeContextProvider>
      <VideoRefMapContextProvider>
        <div className="flex h-full flex-col">
          {showHeader && <SidebarHeader />}
          <SidebarTitle
            title={libraryItem.name.myTranslation}
            icon={<FileIcon className="size-[18px] text-gray-9" />}
            sidebarType={DetailSidebarType.TraineeLibraryItem}
          />
          <SidebarContent>
            <div className="flex h-full min-h-0 flex-col gap-4">
              {/* Description */}
              {libraryItem.description?.myTranslation && (
                <div className="rounded-lg bg-gray-1 p-4">
                  <RichTextPreview
                    text={libraryItem.description.myTranslation}
                    isSelected={true}
                    className="text-sm text-gray-10"
                  />
                </div>
              )}

              {/* Rich Text Document Content */}
              {isRichTextDocument && richTextContent && (
                <Card className="flex flex-col rounded-lg border bg-card p-4">
                  <ReadOnlyTrainingRichTextDocument text={richTextContent} />
                </Card>
              )}

              {/* PDF Preview - Full width with open button */}
              {!isRichTextDocument && mediaUrl && isPdf && (
                <div className="flex min-h-0 flex-1 flex-col gap-3 pb-4">
                  <Button
                    variant="outline"
                    className="w-full shrink-0"
                    onClick={() => window.open(mediaUrl, "_blank")}
                  >
                    <ExternalLinkIcon className="mr-2 size-4" />
                    Open PDF in new tab
                  </Button>
                  <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-3">
                    <iframe
                      src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(mediaUrl)}#pagemode=none&toolbar=0`}
                      title="PDF Preview"
                      className="size-full border-none"
                    />
                  </div>
                </div>
              )}

              {/* Media Content (Video, Image, Link, etc.) */}
              {!isRichTextDocument && mediaUrl && !isPdf && (
                <div className="overflow-hidden rounded-lg">
                  <MediaPreview
                    id={trainingResource.id}
                    mediaUrl={mediaUrl}
                    showViewButton={true}
                  />
                </div>
              )}

              {/* Empty state when no content */}
              {!publishedVersion && (
                <div className="flex flex-col items-center justify-center rounded-lg bg-gray-1 p-8">
                  <FileIcon className="mb-2 size-8 text-gray-5" />
                  <Text type="P2" className="text-center text-muted-foreground">
                    This resource has not been published yet.
                  </Text>
                </div>
              )}
            </div>
          </SidebarContent>
        </div>
      </VideoRefMapContextProvider>
    </TranslationModeContextProvider>
  );
};

gql`
  query TraineeLibraryItemSidebar($id: UUID!) {
    LibraryItem(id: $id) {
      id
      name {
        ...MyTranslationSet
      }
      description {
        ...MyTranslationSet
      }
      trainingResource {
        id
        publishedVersion {
          id
          type
          richTextDocument {
            id
            content {
              ...MyTranslationSet
            }
          }
          media {
            id
            updatedAt
            ...PromptMedia
          }
        }
      }
    }
  }
`;

export default TraineeLibraryItemDetailSidebar;
